// Lib
import { includes, groupBy, mapValues, set, head, debounce, isNumber } from 'lodash';
import { get, isEmpty } from 'lodash/fp';
import globalLogger from '../../logger/logger';

const logger = globalLogger.createChannel('element.move');

// Utils
import { getConnectingElementIds } from '../connections/elementConnectionsUtils';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import { getNewScores } from '../../../common/elements/listOrderScores/scoreCalculator';
import { isBoard, isBoardLike } from '../../../common/elements/utils/elementTypeUtils';
import {
    addRestoreLocationForMovesToTrash,
    isLocationCanvas,
    isLocationInbox,
    isLocationTrash,
    removeTrashSuffix,
} from '../../../common/elements/utils/elementLocationUtils';
import { isAsyncEntityFetched } from '../../utils/services/http/asyncResource/asyncResourceUtils';
import {
    getPhysicalAncestorIds,
    getElement as getElementFromElementsMap,
} from '../../../common/elements/utils/elementTraversalUtils';
import { numbersAscending } from '../../../common/utils/sortUtil';
import { getAllOverlappingElements } from './elementMoveActionUtil';
import {
    getChildrenViaGraph,
    getSortedChildrenInSectionViaGraph,
} from '../../../common/elements/utils/elementGraphUtils';
import { sortByScoreProperty } from '../../../common/elements/utils/elementSortUtils';
import { getSelectedElements } from '../selection/selectedElementsSelector';
import {
    getScore,
    getXPosition,
    getYPosition,
    getElementId,
    getElementLocation,
    getLocationPosition,
    isElementLocked,
    getLocationParentId,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getAclIdsSelector } from '../../utils/permissions/permissionsSelector';
import { getPermission } from '../../../common/permissions/elementPermissionsUtil';
import { ensureElementPositionIsInteger } from '../../../common/elements/utils/elementPositionUtils';
import { validateElementTypeSavePermission } from '../../../common/elements/utils/elementTypePermissionsUtils';
import { toArray } from '../../../common/utils/immutableHelper';

import * as baseElementActions from '../../../common/elements/elementActions';

// Selectors
import { elementGraphSelector } from '../selectors/elementGraphSelector';
import { getElement, getElements } from '../selectors/elementSelector';
import { boardResourcesSelector } from '../board/boardSelector';
import { getCurrentBoardId } from '../selectors/currentBoardSelector';

// Measurements
import { getMeasurementsMap } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';

// Actions
import { setInboxFromMultiElementMove } from '../../inbox/inboxActions';
import { createTaskInEmptyTaskListsAfterMoves } from '../taskList/taskListMoveActions';
import { deselectAllElements } from '../selection/selectionActions';
import { handleConnectedElementMoves } from '../connections/elementConnectionsActions';

// Constants
import * as ELEMENTS_ACTION_TYPE from '../../../common/elements/elementsConstants';
import { BoardSections } from '../../../common/boards/boardConstants';
import { PERMISSION_VALUES } from '../../../common/permissions/permissionConstants';
import { ELEMENT_MOVE_OPERATIONS } from '../../../common/elements/elementConstants';

/**
 * Returns an element's original location from the pre-move state.
 */
const getFromLocation = (initialState, elementId) => {
    const initialElementState = getElement(initialState, { elementId });
    return initialElementState.get('location').toJS();
};

/**
 * Adds original location metadata onto a move for downstream consumers.
 */
const addFromLocation = (initialState) => (move) => {
    move.from = getFromLocation(initialState, move.id);
    return move;
};

/**
 * Builds the score-group key for inbox-style moves.
 */
export const getInboxMoveScoreGroupKey = (move) => `${move.location.parentId}.${move.location.position.index}`;

/**
 * Builds the score-group key for canvas stacking moves.
 */
const getCanvasMoveScoreGroupKey = (move) => `${move.location.position.group}`;

/**
 * Determines whether a move needs a client-side score recalculation.
 */
const moveRequiresScoreCalculation = (elements, boardResourcesState) => (move) => {
    const { location } = move;

    // If it already has a score, use it rather than calculate it again (useful when undoing)
    if (location.position.score != null) return false;

    // check to see if we have access to the children of the board that's having a child added to its inbox
    const parentBoardResource = boardResourcesState?.[location.parentId];
    const parent = elements.get(location.parentId);

    // If we don't have it, then we can't calculate the score of the new item, so we just leave it as null for the
    // server to take care of
    if (!parent || (isBoard(parent) && !isAsyncEntityFetched(parentBoardResource))) return false;

    return true;
};

/**
 * Creates an action to reset scores within a destination parent and section.
 */
const resetScores = ({ parentId, section }) => ({
    type: ELEMENTS_ACTION_TYPE.ELEMENTS_RESET_SCORES,
    parentId,
    section,
    sync: true,
});

/**
 * This function returns the elements that a move group will need to position themselves amongst.
 * For example, a move to an INBOX will require all other inbox elements to check scores against.
 * For a move to the CANVAS all overlapping elements will be required to check scores against.
 */
const getGroupDestinationElements = (getState, moveGroup, measurements) => {
    const state = getState();

    const firstMove = head(moveGroup);
    const { section, parentId } = get('location', firstMove);

    const elements = getElements(state);
    const elementGraph = elementGraphSelector(state);
    const currentParentElements = getChildrenViaGraph({ elements, elementGraph, parentId }).valueSeq().toJS();

    if (section === BoardSections.INBOX) return currentParentElements.filter(isLocationInbox);

    return getAllOverlappingElements(moveGroup, currentParentElements, measurements);
};

/**
 * Calculates the new scores required to insert a move group at its destination index.
 */
const getGroupScores = (getState, moveGroup, measurements) => {
    const { location } = head(moveGroup);

    const currentScores = getGroupDestinationElements(getState, moveGroup, measurements)
        .map(getScore)
        .sort(numbersAscending);

    return getNewScores(currentScores, location.position.index, moveGroup.length);
};

/**
 * Precomputes score batches for move groups that require client-side scoring.
 */
const buildScoreMap =
    (dispatch, getState, { measurementsStore }) =>
    (moves) => {
        const measurementsState = measurementsStore.getState();
        const measurements = getMeasurementsMap(measurementsState);

        const state = getState();
        const elements = state.get('elements');
        const boardResourcesState = boardResourcesSelector(state);

        const movesRequiringScoreCalculation = moves.filter(
            moveRequiresScoreCalculation(elements, boardResourcesState),
        );

        // Group moves by parentId.index
        const allInbox = moves.every((move) => move.location.section === BoardSections.INBOX);
        const groupedMoves = allInbox
            ? groupBy(movesRequiringScoreCalculation, getInboxMoveScoreGroupKey)
            : groupBy(movesRequiringScoreCalculation, getCanvasMoveScoreGroupKey);

        // Calculate the scores for each one of those groups
        return mapValues(groupedMoves, (moveGroup) => {
            let newScores = getGroupScores(getState, moveGroup, measurements);

            // If the score is null a collision has occurred, so we need to refresh all elements scores before attempting
            // to insert this new / moved element
            if (newScores == null) {
                dispatch(resetScores(head(moveGroup).location));

                // Try to calculate the scores again
                newScores = getGroupScores(getState, moveGroup, measurements) || [];
            }

            return {
                currentIndex: 0,
                newScores,
            };
        });
    };

/**
 * Updates the location object with a score if necessary.
 * The score is used in both the INBOX and the CANVAS to determine their order.
 * In the INBOX a higher score will mean they are lower in the list.
 * In the CANVAS a higher score will mean they will sit on top of other elements.
 *
 * If a score collision happens (i.e. two elements end up with the same score) this method will also dispatch the
 * 'ELEMENTS_RESET_SCORES' action which will recalculate the scores for every element and then attempt to calculate the
 * score again.
 */
export const updateLocation =
    (dispatch, getState, { measurementsStore }) =>
    (moves) => {
        const scoreMap = buildScoreMap(dispatch, getState, { measurementsStore })(moves);

        const state = getState();
        const elements = state.get('elements');
        const boardResourcesState = boardResourcesSelector(state);

        return (move) => {
            if (!moveRequiresScoreCalculation(elements, boardResourcesState)(move)) return move;

            const scoreMapKey =
                move.location.section === BoardSections.INBOX
                    ? getInboxMoveScoreGroupKey(move)
                    : getCanvasMoveScoreGroupKey(move);

            const scoreEntry = scoreMap[scoreMapKey];
            const { currentIndex = 0, newScores = [] } = scoreEntry;
            set(move, 'location.position.score', newScores[currentIndex]);
            set(scoreEntry, 'currentIndex', currentIndex + 1);

            return move;
        };
    };

/**
 * Ensures that a move won't result in a recursive structure.
 * NOTE: This only checks the initial state, not the end state! It would be possible to
 * configure a move such that a recursive structure was possible.
 */
const checkMoveSafety = (state) => (move) => {
    const elements = getElements(state);

    if (move.location.parentId.indexOf(move.id) !== -1) {
        logger.warn('Paradox: Unable to perform move as element is being moved onto itself', { move });
        return false;
    }

    const ancestorIds = getPhysicalAncestorIds(elements, move.location.parentId);

    // If the moved element exists in the ancestors of the
    if (includes(ancestorIds, move.id)) {
        logger.warn('Paradox: Unable to perform move as moved element exists in location ancestry', { move });
        return false;
    }

    const movedElement = getElementFromElementsMap(elements, move.id);
    const currentParentId = removeTrashSuffix(getLocationParentId(movedElement));
    const targetParentId = removeTrashSuffix(move.location.parentId);

    // Ensure we have the correct permission
    const aclIds = getAclIdsSelector(state);
    // Trashed element's original parents might not be loaded into the client store, so just assume that
    // the user has permission to move the element if it's in their trash, as they shouldn't be able to
    // view the element unless they do have permission to view the source board
    const sourcePermissions = isLocationTrash(movedElement)
        ? PERMISSION_VALUES.FULL_ACCESS
        : getPermission(elements, currentParentId, aclIds);
    const targetPermissions = getPermission(elements, targetParentId, aclIds);

    const canMoveFromSource = validateElementTypeSavePermission(sourcePermissions, movedElement);
    const canMoveToTarget = validateElementTypeSavePermission(targetPermissions, movedElement);

    return canMoveFromSource && canMoveToTarget;
};

/**
 * Returns an element's order within other moves targeting the same destination insertion point.
 */
const moveOrderWithinDestinationGroupCache = new WeakMap();

export const getMoveOrderWithinDestinationGroup = ({ moves, move }) => {
    // Defensive guard: if moves is not an array, fall back to 0.
    if (!Array.isArray(moves)) return 0;

    // Attempt to retrieve a cached order map for this specific moves array.
    let orderById = moveOrderWithinDestinationGroupCache.get(moves);

    // If no cache exists yet, build it in a single pass and cache the result.
    if (!orderById) {
        orderById = {};
        const groupCounts = {};

        moves.forEach((currentMove) => {
            if (!currentMove) return;

            const currentLocation = currentMove.location;
            if (!currentLocation || !currentLocation.position) return;

            const { parentId, section, position } = currentLocation;
            const index = position.index;

            // Build a key that uniquely identifies the destination insertion point.
            const groupKey = `${parentId || ''}::${section || ''}::${index}`;

            const currentCount = groupCounts[groupKey] || 0;
            groupCounts[groupKey] = currentCount + 1;

            // The order within the destination group is the number of prior moves in the same group.
            orderById[currentMove.id] = currentCount;
        });

        moveOrderWithinDestinationGroupCache.set(moves, orderById);
    }

    const order = orderById[move.id];
    return isNumber(order) ? order : 0;
};

/**
 * Returns whether the full inbox destination group needs to move to achieve the requested order.
 */
export const doesInboxDestinationGroupRequireMove = ({ inboxElements, moves, move }) => {
    if (!Array.isArray(moves)) return true;

    const targetLocation = move?.location;
    const targetIndex = targetLocation?.position?.index;

    if (!targetLocation || !isNumber(targetIndex)) return true;

    // Compare only the moves that land at this exact inbox insertion point.
    const destinationGroupMoves = moves.filter((currentMove) => {
        const currentLocation = currentMove?.location;

        return (
            currentLocation?.parentId === targetLocation.parentId &&
            currentLocation?.section === targetLocation.section &&
            currentLocation?.position?.index === targetIndex
        );
    });

    // Preserve the move-group order so we can compare it to the current inbox slice.
    const destinationGroupMoveIds = destinationGroupMoves
        .slice()
        .sort(
            (moveA, moveB) =>
                getMoveOrderWithinDestinationGroup({ moves, move: moveA }) -
                getMoveOrderWithinDestinationGroup({ moves, move: moveB }),
        )
        .map((currentMove) => currentMove.id);

    // Read the current inbox elements that already occupy the target range.
    const currentElementIdsInDestinationRange = toArray(inboxElements)
        .slice(targetIndex, targetIndex + destinationGroupMoveIds.length)
        .map(getElementId);

    if (currentElementIdsInDestinationRange.length !== destinationGroupMoveIds.length) return true;

    return destinationGroupMoveIds.some((elementId, index) => currentElementIdsInDestinationRange[index] !== elementId);
};
/**
 * If the move being performed doesn't actually change anything (i.e. an element is dropped onto the same position
 * in a list) then don't perform the move, as it's a wasted effort.
 */
const shouldMove = (state, moves) => (move) => {
    const { id, location } = move;

    // Can't move an element that we don't know about
    const element = getElement(state, { elementId: id });
    if (!element) return false;

    if (location.section !== BoardSections.INBOX) return true;

    const elements = getElements(state);

    const elementGraph = elementGraphSelector(state);
    const inboxElements = getSortedChildrenInSectionViaGraph({
        elements,
        elementGraph,
        parentId: location.parentId,
        section: location.section,
    });

    return doesInboxDestinationGroupRequireMove({ inboxElements, moves, move });
};

/**
 * Deselects all elements if they're dropped onto a board.
 * NOTE: This also assumes that all moves
 */
const deselectOnBoardDrop =
    ({ moves, transactionId }) =>
    (dispatch, getState) => {
        const fromParentId = get([0, 'from', 'parentId'], moves);
        const destinationParentId = get([0, 'location', 'parentId'], moves);

        // Going to the same parentId so ignore
        if (fromParentId === destinationParentId) return;

        const state = getState();
        const currentBoardId = getCurrentBoardId(state);

        // If we're dropping onto the current board, ignore
        if (currentBoardId === destinationParentId) return;

        const element = getElement(state, { elementId: destinationParentId });

        if (isBoardLike(element)) dispatch(deselectAllElements({ transactionId }));
    };

/**
 * Dispatches a move event for an element, given the location to move the element to.
 * The location's position will be updated with a 'score' if necessary.
 */
export const moveMultipleElements =
    ({ moves, sync = true, transactionId = getNewTransactionId(), moveOperation, initialMeasurements, actionOrigin }) =>
    (dispatch, getState, { measurementsStore }) => {
        // Add the from location to the moves
        const initialState = getState();

        const isSafeMultiMove = moves.every(checkMoveSafety(initialState));
        if (!isSafeMultiMove) {
            // TODO Dispatch a warning action that displays a message explaining why the move failed
            logger.warn('Preventing moves as a paradox will occur');
            return;
        }

        // TODO - Might be a good idea to do some clamping to prevent crazy numbers when moving.
        //  E.g. No more than the current canvasOrigin - A or canvasSize + B?

        let updatedMoves = moves
            .filter(shouldMove(initialState, moves))
            // NOTE: This assumes that all moves are going to the same parentId & section.  If this assumption becomes
            //  incorrect, further work will need to be done here
            .map(updateLocation(dispatch, getState, { measurementsStore })(moves))
            .map(ensureElementPositionIsInteger)
            .map(addFromLocation(initialState))
            .map(addRestoreLocationForMovesToTrash);

        // If we no longer need to perform any moves, just finish
        if (!updatedMoves.length) return;

        updatedMoves = dispatch(handleConnectedElementMoves({ moves: updatedMoves }));

        dispatch(
            baseElementActions.moveMultipleElements({
                moves: updatedMoves,
                initialMeasurements,
                sync,
                transactionId,
                moveOperation,
                actionOrigin,
            }),
        );

        // if you're dropping on a board that isn't the current one, open up it's unsorted notes
        dispatch(setInboxFromMultiElementMove({ moves: updatedMoves, transactionId }));

        // If you've moved all the Tasks out of a TaskList then add a new Task to that TaskList
        dispatch(createTaskInEmptyTaskListsAfterMoves({ moves: updatedMoves, transactionId }));

        dispatch(deselectOnBoardDrop({ moves: updatedMoves, transactionId }));
    };

/**
 * Dispatches an immediate synced move request.
 */
const syncMoves = ({ moves, moveOperation, dispatch }) => dispatch(moveMultipleElements({ moves, moveOperation }));

/**
 * Batches rapid move syncs into a single debounced request.
 */
const debouncedSyncMoves = debounce(syncMoves, 500);

/**
 * Converts an x/y keyboard nudge into a list movement direction.
 */
const getShiftDirection = ({ x = 0, y = 0 }) => {
    if (x < 0 || y < 0) return -1;
    if (x > 0 || y > 0) return 1;
    return 0;
};

/**
 * Calculates how many stepwise moves a keyboard nudge should perform.
 */
const getShiftMagnitude = ({ x = 0, y = 0 }) => Math.max(Math.abs(x), Math.abs(y));

/**
 * Returns a new array with two indexes swapped.
 */
const swapPositions = (array, indexA, indexB) => {
    const nextArray = [...array];
    [nextArray[indexA], nextArray[indexB]] = [nextArray[indexB], nextArray[indexA]];
    return nextArray;
};

/**
 * Groups selected sibling IDs into contiguous runs in their current order.
 */
const getSelectedRuns = ({ siblingIds, selectedElementIds }) => {
    const selectedElementIdSet = new Set(selectedElementIds);
    const selectedRuns = [];
    let currentRun = null;

    siblingIds.forEach((elementId, index) => {
        if (!selectedElementIdSet.has(elementId)) {
            currentRun = null;
            return;
        }

        if (!currentRun) {
            currentRun = {
                elementIds: [],
                startIndex: index,
                endIndex: index,
            };
            selectedRuns.push(currentRun);
        }

        currentRun.elementIds.push(elementId);
        currentRun.endIndex = index;
    });

    return selectedRuns;
};

/**
 * Simulates arrow-key list reordering by swapping each selected item with its adjacent sibling.
 */
export const reorderSelectedElementIds = ({ siblingIds, selectedElementIds, direction, magnitude = 1 }) => {
    if (!siblingIds.length || !selectedElementIds.length || !direction || magnitude < 1) return siblingIds;

    const selectedElementIdSet = new Set(selectedElementIds);
    let reorderedSiblingIds = [...siblingIds];

    for (let step = 0; step < magnitude; step += 1) {
        let hasMoved = false;

        if (direction < 0) {
            for (let index = 1; index < reorderedSiblingIds.length; index += 1) {
                const currentElementId = reorderedSiblingIds[index];
                const previousElementId = reorderedSiblingIds[index - 1];

                if (!selectedElementIdSet.has(currentElementId) || selectedElementIdSet.has(previousElementId))
                    continue;

                reorderedSiblingIds = swapPositions(reorderedSiblingIds, index - 1, index);
                hasMoved = true;
            }
        } else {
            for (let index = reorderedSiblingIds.length - 2; index >= 0; index -= 1) {
                const currentElementId = reorderedSiblingIds[index];
                const nextElementId = reorderedSiblingIds[index + 1];

                if (!selectedElementIdSet.has(currentElementId) || selectedElementIdSet.has(nextElementId)) continue;

                reorderedSiblingIds = swapPositions(reorderedSiblingIds, index, index + 1);
                hasMoved = true;
            }
        }

        if (!hasMoved) return reorderedSiblingIds;
    }

    return reorderedSiblingIds;
};

/**
 * Reorders selected list items to the top or bottom while preserving their relative order.
 */
export const reorderSelectedElementIdsToBoundary = ({ siblingIds, selectedElementIds, direction }) => {
    if (!siblingIds.length || !selectedElementIds.length || !direction) return siblingIds;

    const selectedElementIdSet = new Set(selectedElementIds);
    const selectedSiblingIds = siblingIds.filter((elementId) => selectedElementIdSet.has(elementId));
    const unselectedSiblingIds = siblingIds.filter((elementId) => !selectedElementIdSet.has(elementId));

    return direction < 0
        ? [...selectedSiblingIds, ...unselectedSiblingIds]
        : [...unselectedSiblingIds, ...selectedSiblingIds];
};

/**
 * Builds inbox-style move actions for a same-parent list selection.
 */
export const getSelectedInboxElementMoves = ({ selectedElements, siblingElements, direction, magnitude = 1 }) => {
    const selectedElementArray = toArray(selectedElements) || [];
    const siblingElementArray = toArray(siblingElements) || [];

    if (!selectedElementArray.length || !siblingElementArray.length || !direction || magnitude < 1) return [];

    const firstSelectedElement = selectedElementArray[0];
    const parentId = getLocationParentId(firstSelectedElement);
    const selectedElementIds = selectedElementArray.map(getElementId);
    const siblingIds = siblingElementArray.map(getElementId);
    const pinnedBoundaryElementId = direction < 0 && selectedElementIds.includes(siblingIds[0]) ? siblingIds[0] : null;
    // On shift+arrow the elements will be sent to the top or bottom of the list
    const reorderedSiblingIds =
        magnitude > 1
            ? reorderSelectedElementIdsToBoundary({
                  siblingIds,
                  selectedElementIds,
                  direction,
              })
            : reorderSelectedElementIds({
                  siblingIds,
                  selectedElementIds,
                  direction,
                  magnitude,
              });

    if (reorderedSiblingIds.every((elementId, index) => siblingIds[index] === elementId)) return [];

    const selectedRuns = getSelectedRuns({
        siblingIds: reorderedSiblingIds,
        selectedElementIds,
    });

    const newIndexMap = selectedRuns.reduce((acc, { elementIds, startIndex, endIndex }) => {
        // Keep a pinned top element out of the move payload while preserving the remaining run order.
        const movableElementIds = pinnedBoundaryElementId
            ? elementIds.filter((elementId) => elementId !== pinnedBoundaryElementId)
            : elementIds;
        const pinnedElementCount = elementIds.length - movableElementIds.length;
        // Shift the shared insertion point past any pinned element that remains at the boundary.
        const insertionIndex = direction < 0 ? startIndex + pinnedElementCount : endIndex + 1;

        if (!movableElementIds.length) return acc;

        movableElementIds.forEach((elementId) => {
            acc[elementId] = insertionIndex;
        });

        return acc;
    }, {});

    return siblingElementArray
        .filter(
            (element) =>
                selectedElementIds.includes(getElementId(element)) && getElementId(element) !== pinnedBoundaryElementId,
        )
        .map((element) => ({
            id: getElementId(element),
            location: {
                parentId,
                position: {
                    index: newIndexMap[getElementId(element)],
                },
                section: BoardSections.INBOX,
            },
        }));
};

/**
 * Reorders selected inbox elements within a shared parent using keyboard movement semantics.
 */
const shiftInboxElements =
    ({ selectedElements, direction, magnitude, transactionId }) =>
    (dispatch, getState) => {
        const state = getState();
        const selectedInboxElements = selectedElements.filter(
            (element) => isLocationInbox(element) && !isElementLocked(element),
        );

        if (selectedInboxElements.size !== selectedElements.size || !selectedInboxElements.size) return;

        const firstSelectedElement = selectedInboxElements.first();
        const parentId = getLocationParentId(firstSelectedElement);
        const allElementsShareParent = selectedInboxElements.every(
            (element) => getLocationParentId(element) === parentId,
        );

        if (!allElementsShareParent) return;

        const elements = getElements(state);
        const elementGraph = elementGraphSelector(state);
        const siblingElements = getSortedChildrenInSectionViaGraph({
            elements,
            elementGraph,
            parentId,
            section: BoardSections.INBOX,
            sortFn: sortByScoreProperty,
        });

        const moves = getSelectedInboxElementMoves({
            selectedElements: selectedInboxElements,
            siblingElements,
            direction,
            magnitude,
        });

        if (!moves.length) return;

        return dispatch(
            moveMultipleElements({
                moves,
                moveOperation: ELEMENT_MOVE_OPERATIONS.NUDGE,
                transactionId,
            }),
        );
    };

/**
 * Nudges selected canvas elements, including connected endpoints, by the requested delta.
 */
const shiftCanvasElements =
    ({ selectedElements, selectedCanvasElements, x, y, transactionId }) =>
    (dispatch, getState) => {
        const state = getState();
        const selectedElementIds = toArray(selectedElements.map(getElementId));
        let movedElements = selectedCanvasElements;

        // If there's any lines selected, then also select their connected element IDs
        const connectedElementIds = getConnectingElementIds(selectedElements);

        if (!isEmpty(connectedElementIds)) {
            const elements = getElements(state);

            connectedElementIds.forEach((connectedElementId) => {
                if (selectedElementIds.includes(connectedElementId)) return;

                const connectedElement = getElementFromElementsMap(elements, connectedElementId);

                // If the connected element could not be found in the elements map, don't shift it
                if (connectedElement) {
                    movedElements = movedElements.push(connectedElement);
                }
            });
        }

        // For now just work with canvas elements
        const moves = movedElements
            .map((element) => ({
                id: getElementId(element),
                location: {
                    ...getElementLocation(element).toJS(),
                    position: {
                        ...getLocationPosition(element).toJS(),
                        x: getXPosition(element) + x,
                        y: getYPosition(element) + y,
                    },
                },
            }))
            .toArray();

        if (!moves.length) return;

        debouncedSyncMoves({ moves, moveOperation: ELEMENT_MOVE_OPERATIONS.NUDGE, dispatch });

        return dispatch(
            moveMultipleElements({
                moves,
                moveOperation: ELEMENT_MOVE_OPERATIONS.NUDGE,
                sync: false,
                transactionId,
            }),
        );
    };

/**
 * Routes arrow-key moves to inbox reordering or canvas nudging for the current selection.
 */
export const shiftSelectedElements =
    ({ x = 0, y = 0, transactionId = getNewTransactionId() }) =>
    (dispatch, getState) => {
        // Then get the selected elements again and perform the move
        const state = getState();
        const selectedElements = getSelectedElements(state);
        const direction = getShiftDirection({ x, y });
        const magnitude = getShiftMagnitude({ x, y });

        if (!direction || !magnitude) return;

        const selectedCanvasElements = selectedElements.filter((element) => {
            if (!isLocationCanvas(element)) return false;
            if (isElementLocked(element)) return false;
            return true;
        });

        return selectedCanvasElements.size < 1
            ? dispatch(shiftInboxElements({ selectedElements, direction, magnitude, transactionId }))
            : dispatch(shiftCanvasElements({ selectedElements, selectedCanvasElements, x, y, transactionId }));
    };
